import { useCallback, useEffect, useReducer, useRef } from 'react';

import { transport } from '@/api/transport';
import { ApiError, type ApiErrorCode, type BridgeEvent, type BridgeView, type EncryptedFile, type Message, type NewMessage, type PresenceSide, type Progress, type Role } from '@/api/types';
import type { ConnectRequest } from '@/components/ShareOverlay.types';
import { peekMessage, releasePosition } from '@/crypto';
import { thisDevice } from '@/lib/nearby';
import { useAppPush } from '@/lib/useAppPush';
import { getClaimToken, getOwnerToken, setClaimToken, setOwnerToken, setPurpose } from '@/lib/storage';

type BridgeStatus = 'loading' | 'ready' | 'error';

/** A message the server did not take gives its number in the writer's run back, so a failed send leaves no hole. */
function giveBack(bridgeId: string, envelope: string): void {
  try {
    releasePosition(bridgeId, peekMessage(envelope).position);
  } catch {
    // not an envelope of this version: it has no number
  }
}

export interface BridgeState {
  readonly status: BridgeStatus;
  readonly view: BridgeView | null;
  readonly token: string | null;
  readonly role: Role | null;
  readonly error: ApiErrorCode | null;
  readonly online: boolean;
  readonly connectRequest: ConnectRequest | null;
  /** Who has the bridge open right now, side by side; null until the server says. */
  readonly presence: Readonly<Record<Role, PresenceSide>> | null;
  /** Set once the bridge was deleted while open (by the owner or by expiry). */
  readonly gone: 'deleted' | 'expired' | null;
}

type Action =
  | { type: 'loaded'; view: BridgeView; token: string; role: Role }
  | { type: 'failed'; error: ApiErrorCode }
  | { type: 'event'; event: BridgeEvent }
  | { type: 'expired' }
  | { type: 'message'; message: Message }
  | { type: 'removed'; messageId: string };

const initial: BridgeState = { status: 'loading', view: null, token: null, role: null, error: null, online: false, connectRequest: null, presence: null, gone: null };

function reducer(state: BridgeState, action: Action): BridgeState {
  switch (action.type) {
    case 'loaded':
      return { ...state, status: 'ready', view: action.view, token: action.token, role: action.role, error: null };
    case 'failed':
      return { ...state, status: 'error', error: action.error };
    case 'expired':
      return { ...state, gone: 'expired' };
    case 'message': {
      if (!state.view || state.view.messages.some((m) => m.id === action.message.id)) return state;
      return { ...state, view: { ...state.view, messages: [...state.view.messages, action.message] } };
    }
    case 'removed': {
      if (!state.view) return state;
      return { ...state, view: { ...state.view, messages: state.view.messages.filter((m) => m.id !== action.messageId) } };
    }
    case 'event': {
      const { event } = action;
      switch (event.type) {
        case 'message':
          return reducer(state, { type: 'message', message: event.message });
        case 'message-deleted':
          return reducer(state, { type: 'removed', messageId: event.messageId });
        case 'views':
          return state.view ? { ...state, view: { ...state.view, views: event.views, maxViews: event.maxViews } } : state;
        case 'connect-request':
          return { ...state, connectRequest: { sessionId: event.sessionId, guestPublicKey: event.guestPublicKey, device: event.device, sameNetwork: event.sameNetwork } };
        case 'presence':
          return { ...state, presence: { owner: event.owner, guest: event.guest } };
        case 'deleted':
          return { ...state, gone: state.gone ?? 'deleted', online: false };
        case 'connection':
          return { ...state, online: event.online };
      }
    }
  }
  return state;
}

/**
 * One claim per bridge at a time: a view is spent by the person's click, so a second effect run (strict mode, a quick
 * re-render) joins the claim already on its way instead of making another.
 */
const claiming = new Map<string, Promise<{ claimToken: string; view: BridgeView }>>();

function claimOnce(bridgeId: string): Promise<{ claimToken: string; view: BridgeView }> {
  const running = claiming.get(bridgeId);
  if (running !== undefined) return running;
  const started = transport.claim(bridgeId).finally(() => {
    claiming.delete(bridgeId);
  });
  claiming.set(bridgeId, started);
  return started;
}

function errorCode(error: unknown): ApiErrorCode {
  return error instanceof ApiError ? error.code : 'internal';
}

/** Bound callbacks: safe to pass around as props. */
export interface BridgeActions {
  /** Posts an encrypted text. */
  readonly send: (message: NewMessage) => Promise<void>;
  /** Sends an encrypted file chunk by chunk. */
  readonly sendFile: (file: EncryptedFile, onProgress?: Progress) => Promise<void>;
  /** One encrypted chunk of a file message. */
  readonly fetchChunk: (messageId: string, index: number) => Promise<Uint8Array<ArrayBuffer>>;
  readonly remove: (messageId: string) => Promise<void>;
  readonly destroy: () => Promise<void>;
}

/**
 * Loads a bridge as owner (token in localStorage) or guest (claim token in
 * sessionStorage, otherwise a fresh claim that spends one view), then keeps
 * the view live through the event stream. Nothing is loaded — and no view is
 * spent — until `ready`: the page sets it once the key in the link has been
 * checked and, for a visitor with no token, the person pressed Open secret or Download (or, in a chat, “Open bridge”),
 * so a truncated link or a link scanner that runs the script cannot burn the view.
 */
export function useBridge(bridgeId: string, forceGuest = false, ready = true): BridgeState & BridgeActions {
  const [state, dispatch] = useReducer(reducer, initial);
  const tokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (!ready) return undefined;
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const ownerToken = forceGuest ? null : getOwnerToken(bridgeId);
        if (ownerToken !== null) {
          const view = await transport.getBridge(bridgeId, ownerToken);
          if (!cancelled) {
            tokenRef.current = ownerToken;
            dispatch({ type: 'loaded', view, token: ownerToken, role: 'owner' });
          }
          return;
        }
        const existing = getClaimToken(bridgeId);
        if (existing !== null) {
          try {
            const view = await transport.getBridge(bridgeId, existing);
            if (!cancelled) {
              tokenRef.current = existing;
              dispatch({ type: 'loaded', view, token: existing, role: 'guest' });
            }
            return;
          } catch (error) {
            // A stale claim (server restarted, bridge recreated): claim again.
            if (!(error instanceof ApiError && error.code === 'forbidden')) throw error;
            setClaimToken(bridgeId, null);
          }
        }
        const { claimToken, view } = await claimOnce(bridgeId);
        setClaimToken(bridgeId, claimToken);
        if (!cancelled) {
          tokenRef.current = claimToken;
          dispatch({ type: 'loaded', view, token: claimToken, role: 'guest' });
        }
      } catch (error) {
        const code = errorCode(error);
        // a bridge that is gone leaves no token behind
        if (code === 'not_found' || code === 'expired') {
          setOwnerToken(bridgeId, null);
          setClaimToken(bridgeId, null);
          setPurpose(bridgeId, null);
        }
        if (!cancelled) dispatch({ type: 'failed', error: code });
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [bridgeId, forceGuest, ready]);

  // Live updates once we hold a token.
  useEffect(() => {
    if (state.status !== 'ready' || state.token === null || state.gone !== null) return undefined;
    return transport.subscribe(
      bridgeId,
      state.token,
      (event) => {
        dispatch({ type: 'event', event });
      },
      thisDevice(),
    );
  }, [bridgeId, state.status, state.token, state.gone]);

  // In the native app with notifications allowed: hear about this bridge while the app is closed.
  const push = useAppPush();
  useEffect(() => {
    if (push === null || state.status !== 'ready' || state.token === null || state.gone !== null) return;
    void transport.subscribePush(bridgeId, state.token, push).catch(() => undefined);
  }, [bridgeId, push, state.status, state.token, state.gone]);

  // Countdown to expiry.
  useEffect(() => {
    if (!state.view || state.gone !== null) return undefined;
    const check = (): void => {
      if (state.view && new Date(state.view.expiresAt).getTime() <= Date.now()) dispatch({ type: 'expired' });
    };
    check();
    const timer = window.setInterval(check, 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, [state.view, state.gone]);

  const token = useCallback((): string => {
    if (tokenRef.current === null) throw new ApiError('unauthorized', 'A bridge token is required.');
    return tokenRef.current;
  }, []);

  const send = useCallback(
    async (message: NewMessage) => {
      try {
        const saved = await transport.postMessage(bridgeId, token(), message);
        dispatch({ type: 'message', message: saved });
      } catch (cause) {
        giveBack(bridgeId, message.body);
        throw cause;
      }
    },
    [bridgeId, token],
  );

  const sendFile = useCallback(
    async (file: EncryptedFile, onProgress?: Progress) => {
      try {
        const saved = await transport.postFile(bridgeId, token(), file, onProgress);
        dispatch({ type: 'message', message: saved });
      } catch (cause) {
        giveBack(bridgeId, file.header);
        throw cause;
      }
    },
    [bridgeId, token],
  );

  const fetchChunk = useCallback((messageId: string, index: number) => transport.getChunk({ bridgeId, token: token(), messageId, index }), [bridgeId, token]);

  const remove = useCallback(
    async (messageId: string) => {
      if (tokenRef.current === null) return;
      await transport.deleteMessage({ bridgeId, token: tokenRef.current, messageId });
      dispatch({ type: 'removed', messageId });
    },
    [bridgeId],
  );

  const destroy = useCallback(async () => {
    if (tokenRef.current === null) return;
    await transport.deleteBridge(bridgeId, tokenRef.current);
    dispatch({ type: 'event', event: { type: 'deleted' } });
  }, [bridgeId]);

  return { ...state, send, sendFile, fetchChunk, remove, destroy };
}
